/**
 * Client-side Call Review playback ↔ transcript synchronization.
 * Uses media currentTime (ms), not wall-clock duration, so playback rate stays correct.
 */

export type TranscriptSegmentLike = {
  id: string;
  start_ms: number;
  end_ms: number;
  speaker_role?: string | null;
  text?: string | null;
};

export type NormalizedTranscriptSegment = TranscriptSegmentLike & {
  start_ms: number;
  end_ms: number;
};

/** Prefer provider end_ms; only derive when missing/invalid. */
export function normalizeSegmentTiming(
  seg: TranscriptSegmentLike,
  nextStartMs?: number | null,
): NormalizedTranscriptSegment {
  const start = Math.max(0, Number(seg.start_ms) || 0);
  let end = Number(seg.end_ms);
  if (!Number.isFinite(end) || end <= start) {
    if (nextStartMs != null && Number.isFinite(nextStartMs) && nextStartMs > start) {
      end = Number(nextStartMs);
    } else {
      end = start + 1;
    }
  }
  return { ...seg, start_ms: start, end_ms: end };
}

export function normalizeTranscriptSegments(
  segments: TranscriptSegmentLike[],
): NormalizedTranscriptSegment[] {
  const sorted = [...segments].sort((a, b) => (a.start_ms - b.start_ms) || (a.end_ms - b.end_ms));
  return sorted.map((seg, i) => normalizeSegmentTiming(seg, sorted[i + 1]?.start_ms));
}

/**
 * Binary-search first segment that has not yet ended, then collect overlaps.
 * Active when: start_ms <= currentMs < end_ms
 */
export function findActiveSegments(
  segments: NormalizedTranscriptSegment[],
  currentMs: number,
): NormalizedTranscriptSegment[] {
  if (!segments.length || !Number.isFinite(currentMs) || currentMs < 0) return [];

  // Lowest index with end_ms > currentMs
  let lo = 0;
  let hi = segments.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (segments[mid].end_ms <= currentMs) lo = mid + 1;
    else hi = mid;
  }

  const active: NormalizedTranscriptSegment[] = [];
  for (let i = lo; i < segments.length; i += 1) {
    const seg = segments[i];
    if (seg.start_ms > currentMs) break;
    if (currentMs >= seg.start_ms && currentMs < seg.end_ms) {
      active.push(seg);
    }
  }
  return active;
}

export function activeSpeakerRoles(active: TranscriptSegmentLike[]): string[] {
  const roles = active.map((s) => String(s.speaker_role || 'UNKNOWN').toUpperCase());
  return Array.from(new Set(roles));
}

export function isAgentLikeRole(role?: string | null): boolean {
  const r = String(role || '').toUpperCase();
  return r === 'AGENT' || r === 'COLLECTOR';
}

export function isCustomerLikeRole(role?: string | null): boolean {
  const r = String(role || '').toUpperCase();
  return r === 'CUSTOMER' || r === 'CONTACT';
}

export function isSystemLikeRole(role?: string | null): boolean {
  const r = String(role || '').toUpperCase();
  return r === 'SYSTEM' || r === 'IVR' || r === 'VOICEMAIL';
}

export type SpeakerDisplayLabels = {
  agent: string;
  customer: string;
  system: string;
  unknown: string;
};

export const DEFAULT_SPEAKER_LABELS: SpeakerDisplayLabels = {
  agent: 'Collector',
  customer: 'Contact',
  system: 'System',
  unknown: 'Unknown',
};

export function displaySpeakerRole(
  role?: string | null,
  labels: SpeakerDisplayLabels = DEFAULT_SPEAKER_LABELS,
): string {
  const r = String(role || '').toUpperCase();
  if (isAgentLikeRole(r)) return labels.agent;
  if (isCustomerLikeRole(r)) return labels.customer;
  if (isSystemLikeRole(r)) return labels.system;
  if (r === 'UNKNOWN' || !r) return labels.unknown;
  return role || labels.unknown;
}

export type PlaybackSpeakerStatus =
  | { kind: 'silence' }
  | { kind: 'paused'; atMs: number; roles: string[] }
  | { kind: 'speaking'; roles: string[] };

export function playbackSpeakerStatus(params: {
  currentMs: number;
  playing: boolean;
  activeRoles: string[];
}): PlaybackSpeakerStatus {
  if (!params.activeRoles.length) return { kind: 'silence' };
  if (!params.playing) {
    return { kind: 'paused', atMs: params.currentMs, roles: params.activeRoles };
  }
  return { kind: 'speaking', roles: params.activeRoles };
}

/** Dual-channel master clock helpers — keep physical channels in lockstep. */
export type DualChannelAudioPair = {
  collector: HTMLMediaElement | null;
  contact: HTMLMediaElement | null;
};

/**
 * Seek both channels to the same call-relative moment. `contactOffsetSec` is how far
 * the contact/remote track starts AFTER the collector track (remoteStartOffset -
 * collectorStartOffset, in seconds); the contact element is positioned earlier in its
 * own file by that much so both render the same instant of the call.
 */
export function dualChannelSeek(
  pair: DualChannelAudioPair,
  seconds: number,
  contactOffsetSec = 0,
): void {
  const t = Math.max(0, seconds);
  if (pair.collector) pair.collector.currentTime = t;
  if (pair.contact) pair.contact.currentTime = Math.max(0, t - (contactOffsetSec || 0));
}

export function dualChannelSetPlaybackRate(pair: DualChannelAudioPair, rate: number): void {
  const r = Number.isFinite(rate) && rate > 0 ? rate : 1;
  if (pair.collector) pair.collector.playbackRate = r;
  if (pair.contact) pair.contact.playbackRate = r;
}

export async function dualChannelPlay(pair: DualChannelAudioPair): Promise<void> {
  const tasks: Promise<void>[] = [];
  if (pair.collector) {
    const p = pair.collector.play();
    if (p && typeof (p as Promise<void>).then === 'function') tasks.push(p as Promise<void>);
  }
  if (pair.contact) {
    const p = pair.contact.play();
    if (p && typeof (p as Promise<void>).then === 'function') tasks.push(p as Promise<void>);
  }
  await Promise.all(tasks.map((t) => t.catch(() => undefined)));
}

export function dualChannelPause(pair: DualChannelAudioPair): void {
  pair.collector?.pause();
  pair.contact?.pause();
}

export function dualChannelDriftMs(pair: DualChannelAudioPair, contactOffsetSec = 0): number | null {
  if (!pair.collector || !pair.contact) return null;
  const expectedContact = pair.collector.currentTime - (contactOffsetSec || 0);
  return Math.abs(expectedContact - pair.contact.currentTime) * 1000;
}

export function correctDualChannelDrift(
  pair: DualChannelAudioPair,
  master: 'collector' | 'contact' = 'collector',
  thresholdMs = 40,
  contactOffsetSec = 0,
): boolean {
  const drift = dualChannelDriftMs(pair, contactOffsetSec);
  if (drift == null || drift < thresholdMs) return false;
  // Collector is always the master clock; re-anchor contact to it (respecting the offset).
  if (!pair.collector || !pair.contact) return false;
  if (master === 'collector') {
    pair.contact.currentTime = Math.max(0, pair.collector.currentTime - (contactOffsetSec || 0));
  } else {
    pair.collector.currentTime = pair.contact.currentTime + (contactOffsetSec || 0);
  }
  return true;
}
